import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { createParser } from 'eventsource-parser';
import { ChatMessage } from './ChatComponents';
import { DashboardLayoutAccountSidebar } from '../dashboard-layout/DashboardLayout';
import { LeftPaneMenu } from '../navigation';
import { useAuth } from '../auth/AuthProvider';
import PageLoader from '../../ui-library/PageLoader';
import { feedbackSchema } from '../../schemas/feedbackSchema';
import { useMobileLayout } from '../../contexts/MobileLayoutContext';
import useAnalytics from '@/hooks/useAnalytics';
import { CHAT_SESSION_END_REASON, useAnalyticsSession } from '@/hooks/useAnalyticsSession';
import { ContextService, type ConversationContext } from '@/services/contextService';
import useFollowupPrompts from '@/hooks/useFollowupPrompts';
import { isBallotLoading } from '@/hooks/useBallotLoading';
import type { SuggestedPrompt } from './ChatLandingState';
import useApi from '@/hooks/useApi';
import { ChatWindow } from './ChatWindow';
import { useChat } from '@/hooks/useChat';
import { useSurveyContext } from '@/contexts/SurveyContext';
import { useResearch } from '@/contexts/ResearchContext';
import { useAnnouncement } from '@/providers/AnnouncementProvider';
import { stripMarkdown } from '@/utils/stripMarkdown';
import { forSpeech } from '@/utils/speech';
import { getEmbedSourceUrl } from '@/config/embed';
import { readGuestChat, writeGuestChat, clearGuestChat } from '@/utils/guestChatStorage';
import { rememberChatPath } from '@/utils/lastChatPath';
import { ApiError } from '@/utils/apiClient';
import { chatLimitMessage } from '@/utils/chatLimit';
import { sendWithRetry } from '@/utils/chatSendRetry';
import { foldContextSteps, foldNarration, trailLanguageFor } from './NarrationTypes';
import { rawSourceIdentity as sourceIdentity } from './SourceTypes';
import {
  AnswerDepth,
  DEFAULT_ANSWER_DEPTH,
  asAnswerDepth,
  asResearchDepth,
  depthForRequest,
} from './AnswerDepth';
import { buildChatRequestBody } from '@/services/chatRequest';
import { answerStyleForRequest } from './AnswerStyle';
import { answerStyleStore } from '@/hooks/useAnswerStyle';
import { useAnswerStyleEditor } from '@/contexts/AnswerStyleEditorContext';
import {
  disabledConnectorsForRequest,
  getDisabledConnectors,
} from '@/hooks/useSourceConnectorPrefs';
import { turnPath, waitForTurnResult, type TurnResult } from '@/services/turnRecovery';
import { sendTurnStop, STOP_AWAIT_START_MS } from '@/services/turnStop';
import { guestKeyHeaders, rememberGuestKey } from '@/services/guestKey';
import { historyBubbles, type SavedMessage } from '@/services/conversationHistory';
import type { SendChatOptions } from '@/contexts/ChatContext';

const RETURNING_USER_KEY = 'votemate_returning';

/** One in-flight turn, as far as a Stop needs to know it. */
interface LiveTurn {
  controller: AbortController;
  turnId: string | null;
  conversationId: string | null;
  stopRequested: boolean;
  /** Answer text on screen when the voter stopped. */
  shownChars: number;
  /** Closes a stream stopped before its `start` frame, should none arrive. */
  stopTimer?: ReturnType<typeof setTimeout>;
}

const initialMessages: ChatMessage[] = [];

/** Stable empty list, so a conversation with no taps does not re-key the fetch. */
const EMPTY_ASKED: string[] = [];

/**
 * Which sources a settled answer carries: the cumulative rows its body cites,
 * plus every row this turn produced. Shared by the `end` frame and by a turn
 * read back after its stream was cut off, so both land identically.
 */
function sourcesForMessage(
  text: string,
  incomingSources: any[],
  turnSources: any[]
): any[] | undefined {
  // Expand range citations (e.g. [113–127]) to mirror BotMessage's renderer.
  const expandedText = text.replace(/\[(\d+)\s*[-–—]\s*(\d+)\]/g, (m, a, b) => {
    const lo = Math.min(Number(a), Number(b));
    const hi = Math.max(Number(a), Number(b));
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi - lo > 50) {
      return m;
    }
    const parts: string[] = [];
    for (let n = lo; n <= hi; n += 1) parts.push(`[${n}]`);
    return parts.join('');
  });
  const citedIndices = new Set<number>();
  const citeRegex = /\[(\d+)\]/g;
  let match = citeRegex.exec(expandedText);
  while (match !== null) {
    citedIndices.add(Number(match[1]));
    match = citeRegex.exec(expandedText);
  }
  // Resolve by cite_index. Position is the legacy path, used
  // only when the backend stamped nothing at all — in a
  // stamped array an unstamped entry (an officeholder profile
  // attached after the stream) has no citation number of its
  // own, so letting it claim a position puts a source nothing
  // cites into the panel under another source's marker. This
  // filter and BotMessage's lookup run over the same array, so
  // they resolve markers the same way: a positional match here
  // that BotMessage then refuses is a source in the panel that
  // no chip points at.
  const usesCiteIndex = incomingSources.some((s: any) => typeof s?.cite_index === 'number');
  const filteredSources = incomingSources.filter((s: any, idx: number) => {
    if (!s) return false;
    if (typeof s.cite_index === 'number') return citedIndices.has(s.cite_index);
    return usesCiteIndex ? false : citedIndices.has(idx + 1);
  });
  // Everything this turn produced is kept on the message,
  // cited or not — this array is what `repairCitations`
  // reads to put a source on a claim the model left bare,
  // so a source dropped here is one that can never be
  // cited. The Sources menu is narrowed to what the body
  // ends up citing, in `BotMessage`, once that pass has
  // run. Only THIS turn's extras ride along; the cumulative
  // array is read for cited entries alone, so a marker
  // re-emitted from history still resolves without dragging
  // every source the conversation ever collected into the
  // message (#2114).
  const shownKeys = new Set<string>(filteredSources.map((s: any) => sourceIdentity(s)));
  const uncited = turnSources.filter((s: any) => {
    if (!s) return false;
    const key = sourceIdentity(s);
    if (shownKeys.has(key)) return false;
    shownKeys.add(key);
    return true;
  });
  if (filteredSources.length) {
    return [...filteredSources, ...uncited];
  } else if (turnSources.length) {
    // No inline [N] markers resolved, but this turn still
    // produced sources — e.g. the officeholder answer lists
    // reps as plain text with their Ballotpedia profiles
    // added as supplementary (uncited) sources. Attach only
    // THIS turn's sources (not the cumulative conversation
    // set) so an uncited turn doesn't display sources carried
    // over from earlier turns. This matches the per-turn
    // values the authenticated history reload assigns, so
    // guests (who can't hit that reload path) behave the
    // same as logged-in users.
    return turnSources;
  } else {
    return undefined;
  }
}

const ChatContainer: React.FC = (): React.ReactElement => {
  const { t, i18n } = useTranslation('chat');
  const { id: conversationIdFromUrl } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isInitializing: isAuthInitializing, signIn } = useAuth();
  const query = useApi();
  const { registerChatHandler, setIsProcessing } = useChat();
  const { trackMessageSent } = useSurveyContext();
  const { campaignCode: researchCampaignCode, participantId: researchParticipantId } =
    useResearch();
  const { announce } = useAnnouncement();
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [isResponding, setIsResponding] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isHistoricalConversation, setIsHistoricalConversation] = useState(false);
  const [streamState, setStreamState] = useState({
    event: 'start' as 'start' | 'message' | 'memory_updated' | 'new_conversation' | 'end',
    loading: false,
    loadingMessage: undefined as string | undefined,
  });
  const [feedbackConfirmations, setFeedbackConfirmations] = useState<
    Record<string, 'thumbs_up' | 'thumbs_down'>
  >({});
  // Answer depth for this session. Deliberately component state rather than
  // storage: it sticks across messages so a voter researching in depth does
  // not re-pick every turn, and resets to `auto` on reload so a forgotten
  // `deep` cannot quietly slow every future answer.
  const [depth, setDepth] = useState<AnswerDepth>(DEFAULT_ANSWER_DEPTH);
  const [leftPaneOpen, setLeftPaneOpen] = useState(false);
  const [ballotOpen, setBallotOpen] = useState(true);
  const [triggerLeftPaneAccountMenu, setTriggerLeftPaneAccountMenu] = useState(false);
  const analytics = useAnalytics();
  const { startSession, endSession } = useAnalyticsSession(
    'Chat Session Start',
    'Chat Session End'
  );
  const conversationStartedTrackingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  // The turn a Stop would stop: its stream, and the ids its `start` frame
  // carries. Read by `stopResponse`, which tells the backend (see `turnStop.ts`).
  const liveTurnRef = useRef<LiveTurn | null>(null);
  const endSessionRef = useRef<() => void>(() => endSession(CHAT_SESSION_END_REASON.ROUTE_CHANGE));
  // A guest's signals live in this browser, so hand the ranking the same
  // context this client already builds for chat. A signed-in voter's records
  // are authoritative, so theirs is read server-side instead.
  const [conversationContext, setConversationContext] = useState<ConversationContext | null>(() =>
    ContextService.getContext()
  );
  useEffect(() => ContextService.subscribe(setConversationContext), []);

  const clientContext = user ? undefined : (conversationContext ?? undefined);

  // The completed turn the follow-up chips are for: the last bot answer and
  // the question that produced it. Held back while a reply is still streaming
  // so the chips appear with the finished answer rather than under a partial.
  const lastCompletedTurn = useMemo(() => {
    if (isResponding || streamState.loading) return undefined;
    const lastBotIndex = messages.map((m) => m.sender).lastIndexOf('bot');
    if (lastBotIndex === -1) return undefined;
    const botMessage = messages[lastBotIndex];
    if (!botMessage.text) return undefined;
    const userMessage = [...messages.slice(0, lastBotIndex)]
      .reverse()
      .find((m) => m.sender === 'user');
    return { question: userMessage?.text, answer: botMessage.text };
  }, [messages, isResponding, streamState.loading]);

  // Every prompt tapped in this conversation, so the ranking stops offering
  // questions the voter has already asked. Stored with the conversation it
  // belongs to, so starting or loading another one reads as an empty list
  // without an effect that has to remember to clear it.
  const [asked, setAsked] = useState<{ conversationId: string | null; ids: string[] }>({
    conversationId: null,
    ids: [],
  });
  const askedPromptIds = asked.conversationId === conversationId ? asked.ids : EMPTY_ASKED;
  const rememberAsked = useCallback(
    (promptId?: string) => {
      if (!promptId) return;
      setAsked((prev) =>
        prev.conversationId === conversationId
          ? prev.ids.includes(promptId)
            ? prev
            : { conversationId, ids: [...prev.ids, promptId] }
          : { conversationId, ids: [promptId] }
      );
    },
    [conversationId]
  );

  const followupPrompts = useFollowupPrompts({
    question: lastCompletedTurn?.question,
    answer: lastCompletedTurn?.answer,
    enabled: Boolean(lastCompletedTurn),
    clientContext,
    askedPromptIds,
  });

  const suggestions = useMemo(() => followupPrompts ?? [], [followupPrompts]);

  // Personalization: extract first name and detect returning user.
  // The first name saved in Settings always wins; splitting the OAuth display
  // name is only a fallback for accounts that have not set one. A saved last
  // name with no first name must not fall through to that split — `name` is
  // just the last name in that case, and splitting it would greet the user
  // with it as though it were a first name.
  const userFirstName = useMemo(() => {
    if (user?.first_name) return user.first_name;
    if (user?.last_name) return '';
    if (!user?.name || user.name.includes('@')) return '';
    return user.name.split(' ')[0];
  }, [user?.first_name, user?.last_name, user?.name]);

  useEffect(() => {
    if (!user?.auth0Id) return;
    const key = `${RETURNING_USER_KEY}_${user.auth0Id}`;
    localStorage.setItem(key, 'true');
  }, [user?.auth0Id]);

  // Refs for mutable values used inside handleSend — avoids recreating the
  // callback on every message/stream-state change and breaking React.memo
  // on child components like ChatWindow.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const streamStateRef = useRef(streamState);
  streamStateRef.current = streamState;
  const analyticsRef = useRef(analytics);
  analyticsRef.current = analytics;
  const ballotOpenRef = useRef(ballotOpen);
  ballotOpenRef.current = ballotOpen;
  const userRef = useRef(user);
  userRef.current = user;
  const { retireInviteOnChat } = useAnswerStyleEditor();
  const retireInviteOnChatRef = useRef(retireInviteOnChat);
  retireInviteOnChatRef.current = retireInviteOnChat;

  // Connect to mobile layout context
  const mobileLayout = useMobileLayout();

  // Sync context ballot state with local ballot state
  useEffect(() => {
    setBallotOpen(mobileLayout.ballotOpen);
  }, [mobileLayout.ballotOpen]);

  // End session when user leaves page, stays on site
  useEffect(() => {
    return () => {
      // This should always call the latest version of endSession
      // eslint-disable-next-line react-hooks/exhaustive-deps
      endSessionRef.current();
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  // End session when user leaves site
  useEffect(() => {
    const handleUnload = () => endSession(CHAT_SESSION_END_REASON.APP_CLOSE);
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [endSession]);

  // Start session on page visit
  useEffect(() => {
    const timeout = setTimeout(() => {
      startSession();
      // Artificial delay to give mixpanel time to initialize
    }, 1000);
    return () => clearTimeout(timeout);
  }, [startSession]);

  const isGuestMode = useMemo(() => searchParams.get('guest') === 'true', [searchParams]);
  const effectiveGuestMode = isGuestMode && !user;

  const loadConversation = useCallback(
    async (convId: string) => {
      try {
        setStreamState((prev) => ({ ...prev, loading: true }));
        setIsProcessing(true);
        const response = await query(`/chat/conversation/${convId}`);

        if (!response.ok) {
          throw new Error('Failed to load conversation');
        }

        const conversation = await response.json();

        // Each answer carries its own turn's sources, and a stopped turn reads
        // the way it did live — see `conversationHistory.ts`.
        const chatMessages: ChatMessage[] = historyBubbles(
          Array.isArray(conversation.messages) ? (conversation.messages as SavedMessage[]) : [],
          Array.isArray(conversation.source_logs) ? conversation.source_logs : [],
          t('errors.cancelled')
        ).map((bubble, index) => ({ id: (index + 1).toString(), ...bubble }));

        setMessages(chatMessages);
        setConversationId(convId);
        setIsHistoricalConversation(true);
      } catch (error) {
        console.error('Error loading conversation:', error);
        // If loading fails, start a new conversation
        setMessages(initialMessages);
        setConversationId(null);
        setIsHistoricalConversation(false);

        // Clear the conversation parameter from URL
        navigate(`/chat${effectiveGuestMode ? '?guest=true' : ''}`, { replace: true });
      } finally {
        setStreamState((prev) => ({ ...prev, loading: false }));
        setIsProcessing(false);
      }
    },
    [effectiveGuestMode, navigate, query, setIsProcessing, t]
  );

  // Check authentication on mount with better error handling
  useEffect(() => {
    if (!isAuthInitializing && !user && !isGuestMode) {
      navigate(`/chat?guest=true`, { replace: true });
    }
  }, [user, isAuthInitializing, isGuestMode, searchParams, navigate, signIn]);

  // Only load conversation if user is authenticated and we have a conversation ID from URL
  useEffect(() => {
    if (
      conversationIdFromUrl &&
      conversationIdFromUrl !== conversationId &&
      user &&
      !isAuthInitializing &&
      !streamState.loading
    ) {
      loadConversation(conversationIdFromUrl);
    }
  }, [
    conversationId,
    conversationIdFromUrl,
    loadConversation,
    user,
    isAuthInitializing,
    streamState.loading,
  ]);

  const startNewConversation = useCallback(() => {
    setIsResponding(false);
    abortRef.current?.abort();
    abortRef.current = null;
    setStreamState((prev) => ({ ...prev, loading: false }));
    setIsProcessing(false);
    setMessages(initialMessages);
    setConversationId(null);
    setIsHistoricalConversation(false);
    if (effectiveGuestMode) clearGuestChat();
    navigate(`/chat${isGuestMode ? '?guest=true' : ''}`, { replace: true });
  }, [isGuestMode, effectiveGuestMode, navigate, setIsProcessing]);

  // Remove guest parameter from URL when user is logged in
  useEffect(() => {
    if (user && searchParams.get('guest') === 'true') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('guest');
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [user, searchParams, navigate]);

  // A bare ``/chat`` path means a fresh "new chat". Skip the initial mount run
  // so a returning guest's restored chat (rehydrated below) isn't wiped.
  const chatPathInitializedRef = useRef(false);
  useEffect(() => {
    if (!chatPathInitializedRef.current) {
      chatPathInitializedRef.current = true;
      return;
    }
    if (location.pathname === '/chat') {
      startNewConversation();
    }
  }, [location.pathname, startNewConversation]);

  // Remember the current chat route (incl. ``/chat/:id``) so the Settings/Help
  // back arrows return to this exact conversation, not a fresh chat.
  useEffect(() => {
    if (location.pathname.startsWith('/chat')) {
      rememberChatPath(location.pathname);
    }
  }, [location.pathname]);

  // Guest chat persistence (sessionStorage): guests have no server-side
  // conversation, so restore once on mount and persist settled turns as they go.
  const guestChatRestoredRef = useRef(false);
  useEffect(() => {
    if (guestChatRestoredRef.current || isAuthInitializing) return;
    guestChatRestoredRef.current = true;
    if (!effectiveGuestMode) return;
    const saved = readGuestChat();
    if (saved && saved.messages.length > 0) {
      setMessages(saved.messages);
      setConversationId(saved.conversationId);
    }
  }, [isAuthInitializing, effectiveGuestMode]);

  useEffect(() => {
    // Persist only settled turns, and only after the one-time restore has run,
    // so an empty initial state can't clobber a saved chat before rehydration.
    if (!effectiveGuestMode || streamState.loading || !guestChatRestoredRef.current) return;
    if (messages.length > 0) {
      writeGuestChat(messages, conversationId);
    }
  }, [effectiveGuestMode, streamState.loading, messages, conversationId]);

  useEffect(() => {
    if (user && searchParams.get('state') === 'ballot-open') {
      setBallotOpen(true);
    }
  }, [user, searchParams]);

  // Tell the backend about a Stop, then close the stream. Runs at once when the
  // turn's id is known, and from the `start` frame for a Stop pressed before it.
  const sendStop = useCallback(
    (live: LiveTurn) => {
      clearTimeout(live.stopTimer);
      const conversation = live.conversationId ?? conversationIdRef.current;
      if (live.turnId && conversation) {
        void sendTurnStop(
          { turnId: live.turnId, conversationId: conversation, shownChars: live.shownChars },
          (path, body) =>
            query(path, {
              method: 'POST',
              body: JSON.stringify(body),
              keepalive: true,
              headers: guestKeyHeaders(conversation),
            })
        );
      }
      live.controller.abort();
    },
    [query]
  );

  const handleSend = useCallback(
    async (rawMessage: string, options?: SendChatOptions) => {
      // Trim once so the bubble, the request body, and analytics all carry the
      // same text — matching mobile, whose useChat trims before rendering.
      const message = rawMessage.trim();
      if (!message) {
        throw new Error(
          `Empty message detected! Message: "${rawMessage}" (length: ${rawMessage.length})`
        );
      }

      if (streamStateRef.current.loading || abortRef.current) {
        return;
      }
      // Every send path (composer, chips, follow-ups) lands here, so this holds
      // them all until the ballot, and the chat context built from it, is ready.
      if (isBallotLoading()) {
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const liveTurn: LiveTurn = {
        controller,
        turnId: null,
        conversationId: conversationIdRef.current,
        stopRequested: false,
        shownChars: 0,
      };
      liveTurnRef.current = liveTurn;
      setIsResponding(true);
      const isCurrent = () => abortRef.current === controller && !controller.signal.aborted;
      // Set when a cut-off stream hands over to reading the turn back; Stop
      // aborts whichever of the two `abortRef` holds.
      let recovery: AbortController | null = null;
      const owns = () =>
        abortRef.current === controller || (recovery !== null && abortRef.current === recovery);
      const stoppedWhileRecovering = () => recovery?.signal.aborted ?? false;
      let turnId: string | null = null;
      let recovered = false;
      const currentMessages = messagesRef.current;
      const userMsg: ChatMessage = {
        id: (currentMessages.length + 1).toString(),
        sender: 'user',
        text: message,
      };
      setMessages((prev) => [...prev, userMsg]);
      trackMessageSent();
      setStreamState((prev) => ({ ...prev, loading: true }));
      setIsProcessing(true);

      // Add a loading message for the bot's response
      const botMsgId = (currentMessages.length + 2).toString();
      setMessages((prev) => [...prev, { id: botMsgId, sender: 'bot', text: '', isLoading: true }]);

      const requestStartTime = performance.now();
      // Outcome of this turn for 'Chat Response Received'. Backend-signaled
      // error frames and thrown/aborted requests each overwrite the default.
      let responseStatus: 'success' | 'error' | 'cancelled' = 'success';
      let responseErrorType: string | null = null;
      // A send the API turned away: its status and the id that finds it in the
      // API's logs, so the next one can be traced past the edge. Same fields mobile sends.
      let httpFailure: ApiError | null = null;
      let retried = false;

      // The backend finishes every turn whether or not this client stays
      // connected, so a stream that dies before its `end` frame — a mobile
      // browser backgrounded the tab, the network dropped — reads the saved
      // answer back instead of showing an error. Same flow as mobile's useChat.
      const recoverTurn = async (): Promise<boolean> => {
        const convId = conversationIdRef.current;
        if (recovery || !turnId || !convId || !owns()) return false;
        const waiting = new AbortController();
        recovery = waiting;
        abortRef.current = waiting;
        const result = await waitForTurnResult({
          turnId,
          conversationId: convId,
          signal: waiting.signal,
          fetchTurn: async (id, conversation) => {
            const res = await query(turnPath(id, conversation), {
              signal: waiting.signal,
              headers: guestKeyHeaders(conversation),
            });
            return (await res.json()) as TurnResult;
          },
        });
        if (!result || abortRef.current !== waiting) return false;
        const text = result.text ?? '';
        const sources = Array.isArray(result.sources) ? result.sources : [];
        setMessages((prevMsgs) =>
          prevMsgs.map((msg) =>
            msg.id === botMsgId
              ? {
                  ...msg,
                  text,
                  isLoading: false,
                  loadingMessage: undefined,
                  sources: sourcesForMessage(text, sources, sources),
                }
              : msg
          )
        );
        setStreamState({ event: 'end', loading: true, loadingMessage: undefined });
        announce(t('srResponse', { text: stripMarkdown(text) }));
        recovered = true;
        responseStatus = 'success';
        responseErrorType = null;
        return true;
      };

      try {
        // Get conversation context from sessionStorage
        const context = ContextService.getContext();
        const currentConversationId = conversationIdRef.current;
        // Starting a chat past the answer-style invitation retires it, rather
        // than bringing it back on the next new chat.
        retireInviteOnChatRef.current();
        // Read once, here, as the request starts: a Settings change made while
        // this answer streams applies to the next message, never this one.
        const answerStyle = answerStyleForRequest(answerStyleStore.getSnapshot(), {
          signedIn: !!userRef.current,
        });

        const requestBody = buildChatRequestBody({
          conversationId: currentConversationId ?? null,
          message,
          // Canonicalized in the builder. Drives the AI response language (FR-20).
          language: i18n.language,
          context,
          // Device IANA tz (additive; election dates stay in the voting-area tz).
          clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // Used to track campaigns like research efforts. Comes from URL querystring,
          // sessionStorage, or user record (in that order).
          campaignCode: researchCampaignCode,
          participantId: researchParticipantId,
          // Partner embeds: the host page becomes the answer's first source. Backend
          // honors it only for hosts on that partner's allowlist.
          embedSourceUrl: getEmbedSourceUrl(),
          // Omitted at `auto` — the server's default. A caller (Compare/Explain)
          // can force one turn's depth, overriding the session selector.
          depth: asAnswerDepth(options?.depth) ?? depthForRequest(depth),
          // The ballot row a ballot control sent this from. Absent for a typed
          // message, so the default request stays unchanged.
          ballotRef: options?.ballotRef,
          answerStyle,
          // The Sources switch, read at send time so a flip applies to the
          // very next message. Omitted while every source is on.
          disabledConnectors: disabledConnectorsForRequest(getDisabledConnectors()),
        });

        // Tracked before the request so a send that fails in flight still
        // counts as an attempt — the matching 'Chat Response Received' carries
        // the outcome in `status`.
        analyticsRef.current.track('Chat Message Sent', {
          chat_id: currentConversationId,
          text_length_characters: message.length,
          text_length_words: message.split(/\s+/).length,
          // Always the effective depth, including the `auto` default that is
          // omitted from the request body — otherwise the property only ever
          // reports the minority of turns where someone changed it.
          depth,
          input_method: options?.inputMethod ?? 'text',
          // The style in force (enum values only), so activation and clarity
          // can be read per style.
          answer_wording: answerStyleStore.getSnapshot().style.wording,
          answer_length: answerStyleStore.getSnapshot().style.length,
        });

        // A send turned away before any turn began (see `chatSendRetry`) is
        // repeated once, quietly; the voter only sees the error if that fails too.
        const response = await sendWithRetry(
          () =>
            query(`/chat/stream`, {
              method: 'POST',
              body: JSON.stringify(requestBody),
              signal: controller.signal,
              headers: guestKeyHeaders(currentConversationId),
            }),
          {
            signal: controller.signal,
            isCurrent,
            onRetry: (error, delayMs) => {
              retried = true;
              console.warn(
                `Chat send turned away (HTTP ${error.status}, id ${error.correlationId ?? 'none'}); ` +
                  `retrying in ${delayMs} ms`
              );
            },
          }
        );

        if (!isCurrent()) {
          await response.body?.cancel();
          return;
        }
        if (!response.body) throw new Error('No response body');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let currentSystemMsg = '';
        let done = false;
        let streamEnded = false;

        // eventsource-parser handles chunk boundaries, multi-line `data:`
        // fields, and CRLF normalization per the SSE spec. The `event:` wire
        // field is ignored — every payload carries its own `event` key in
        // the JSON, which is what downstream code dispatches on.
        const parser = createParser({
          onEvent: ({ data }) => {
            if (streamEnded) return;
            const dataStr = data.trim();
            if (!dataStr) return;
            let streamData: any;
            try {
              streamData = JSON.parse(dataStr);
            } catch (err) {
              console.error('SSE: failed to JSON-parse data field:', err, '\ndata:', dataStr);
              return;
            }

            // The turn's ids, read before the UI checks below: a Stop pressed
            // before this frame has detached the UI but still needs them to
            // stop the turn on the server.
            if (streamData.event === 'start') {
              rememberGuestKey(streamData.conversation_id, streamData.guest_key);
              // A guest key that no longer opened the old conversation moved this
              // turn to a new one; Stop and recovery must name it (as mobile does).
              if (streamData.guest_key && streamData.conversation_id) {
                liveTurn.conversationId = streamData.conversation_id;
              }
            }
            if (streamData.event === 'start' && typeof streamData.turn_id === 'string') {
              liveTurn.turnId = streamData.turn_id || null;
              liveTurn.conversationId ??= streamData.conversation_id || null;
              if (liveTurn.stopRequested) {
                // The stopped turn is saved in this conversation, so a chat
                // that had none adopts it — as does a chat still on the
                // conversation the backend replaced — unless another send has begun.
                const replaced =
                  liveTurn.conversationId !== currentConversationId &&
                  conversationIdRef.current === currentConversationId;
                if (
                  (!conversationIdRef.current || replaced) &&
                  liveTurn.conversationId &&
                  !abortRef.current
                ) {
                  conversationIdRef.current = liveTurn.conversationId;
                  setConversationId(liveTurn.conversationId);
                  if (!isGuestMode) {
                    navigate(`/chat/${liveTurn.conversationId}`, { replace: true });
                  }
                }
                sendStop(liveTurn);
                return;
              }
            }
            if (!isCurrent()) return;

            try {
              // A guest key that no longer opens the conversation makes the
              // backend start a new one; follow it so later turns stay together.
              if (
                streamData.event === 'start' &&
                streamData.guest_key &&
                streamData.conversation_id &&
                conversationIdRef.current &&
                conversationIdRef.current !== streamData.conversation_id
              ) {
                conversationIdRef.current = streamData.conversation_id;
                setConversationId(streamData.conversation_id);
                analyticsRef.current.track('Chat Started', { chat_id: streamData.conversation_id });
                if (!isGuestMode) {
                  navigate(`/chat/${streamData.conversation_id}`, { replace: true });
                }
              }
              // Set conversation ID when available
              if (!conversationIdRef.current && streamData.conversation_id) {
                conversationIdRef.current = streamData.conversation_id;
                setConversationId(streamData.conversation_id);

                if (!conversationStartedTrackingRef.current) {
                  analyticsRef.current.track('Chat Started', {
                    chat_id: streamData.conversation_id,
                  });
                  conversationStartedTrackingRef.current = true;
                }

                // Redirect to the conversation URL for new conversations
                if (!isGuestMode) {
                  navigate(`/chat/${streamData.conversation_id}`, {
                    replace: true,
                  });
                }
              }

              // Handle different event types
              switch (streamData.event) {
                case 'start': {
                  if (typeof streamData.turn_id === 'string' && streamData.turn_id) {
                    turnId = streamData.turn_id;
                  }
                  setStreamState({
                    event: 'start',
                    loading: true,
                    loadingMessage: streamData.loading_message || t('initializing'),
                  });
                  // The server's reading of the depth, taken from the first
                  // frame so the label is in place while the answer streams.
                  // Which research agent ran arrives later, on `end`.
                  const startDepth = asAnswerDepth(streamData.answer_depth);
                  if (startDepth) {
                    setMessages((prevMsgs) =>
                      prevMsgs.map((msg) =>
                        msg.id === botMsgId ? { ...msg, answerDepth: startDepth } : msg
                      )
                    );
                  }
                  break;
                }

                case 'narration':
                  if (streamData.system_msg) {
                    // The answer's language rides every narration frame, so
                    // the trail is in the right language from its first line
                    // rather than switching when the answer lands.
                    const trailLanguage = trailLanguageFor(
                      streamData.reply_language,
                      i18n.language
                    );
                    setMessages((prevMsgs) =>
                      prevMsgs.map((msg) =>
                        msg.id === botMsgId
                          ? {
                              ...msg,
                              narrationEvents: foldNarration(msg.narrationEvents || [], streamData),
                              trailLanguage: trailLanguage ?? msg.trailLanguage,
                              isLoading: true,
                            }
                          : msg
                      )
                    );
                  }
                  break;

                case 'message':
                  if (streamData.system_msg) {
                    currentSystemMsg += streamData.system_msg;
                    setMessages((prevMsgs) =>
                      prevMsgs.map((msg) =>
                        msg.id === botMsgId
                          ? {
                              ...msg,
                              text: currentSystemMsg,
                              isLoading: true,
                              loadingMessage: streamStateRef.current.loadingMessage,
                            }
                          : msg
                      )
                    );
                    setStreamState((prev) => ({
                      ...prev,
                      event: 'message',
                      loading: true,
                    }));
                  }
                  break;

                case 'body_reset': {
                  // A tool call arrived after the answer began, so what has
                  // streamed was written without that result. The backend has
                  // the model write the answer again from its first line;
                  // clear what was shown and put the thinking indicator back
                  // until the rewrite's first frame.
                  currentSystemMsg = '';
                  const resetMessage = streamData.loading_message || t('initializing');
                  setMessages((prevMsgs) =>
                    prevMsgs.map((msg) =>
                      msg.id === botMsgId
                        ? { ...msg, text: '', isLoading: true, loadingMessage: resetMessage }
                        : msg
                    )
                  );
                  setStreamState({
                    event: 'start',
                    loading: true,
                    loadingMessage: resetMessage,
                  });
                  break;
                }

                case 'memory_updated':
                  // Backend extracted ≥1 new durable memory from this
                  // turn and persisted it. Tag the in-flight assistant
                  // message so BotMessage renders the "Memory updated"
                  // pill below it. Only fires when memory was actually
                  // retained — turns with no extractable signal (or all
                  // dedup hits) skip this event entirely.
                  setMessages((prevMsgs) =>
                    prevMsgs.map((msg) =>
                      msg.id === botMsgId ? { ...msg, memoryUpdated: true } : msg
                    )
                  );
                  break;

                case 'end': {
                  streamEnded = true;
                  // Filter the backend's cumulative sources to those cited in this message.
                  const incomingSources = Array.isArray(streamData.sources)
                    ? streamData.sources
                    : [];
                  // Sources produced by THIS turn only. Used for the
                  // no-citation fallback below so an uncited turn doesn't
                  // inherit the whole conversation's cumulative source set.
                  // Falls back to the cumulative array for older backends
                  // that don't send this field.
                  const turnSources = Array.isArray(streamData.turn_sources)
                    ? streamData.turn_sources
                    : incomingSources;

                  setMessages((prevMsgs) =>
                    prevMsgs.map((msg) => {
                      if (msg.id !== botMsgId) return msg;
                      const updated: ChatMessage = {
                        ...msg,
                        isLoading: false,
                        loadingMessage: undefined,
                        // Both fields are absent from older backends and from
                        // turns no research agent handled, so keep whatever the
                        // `start` frame already set rather than clearing it.
                        answerDepth: asAnswerDepth(streamData.answer_depth) ?? msg.answerDepth,
                        researchDepth:
                          asResearchDepth(streamData.research_depth) ?? msg.researchDepth,
                        // Sources the turn read from context instead of calling
                        // — the voter's ballot block. They arrive here rather
                        // than on the narration channel because the backend can
                        // only tell they were used once the answer exists;
                        // `foldNarration` slots them ahead of the tool steps so
                        // the trail still reads in the order the turn ran.
                        narrationEvents: foldContextSteps(
                          msg.narrationEvents || [],
                          streamData.context_steps
                        ),
                        trailLanguage:
                          trailLanguageFor(streamData.reply_language, i18n.language) ??
                          msg.trailLanguage,
                      };
                      // If backend ever sends final prose in end.system_msg, prefer the fully
                      // streamed text. But if currentSystemMsg is empty and system_msg looks
                      // meaningful, set it.
                      if (
                        typeof streamData.system_msg === 'string' &&
                        streamData.system_msg &&
                        !currentSystemMsg &&
                        streamData.system_msg !== 'Conversation processing complete.'
                      ) {
                        updated.text = streamData.system_msg;
                      }

                      updated.sources = sourcesForMessage(
                        updated.text ?? '',
                        incomingSources,
                        turnSources
                      );
                      return updated;
                    })
                  );

                  setStreamState({
                    event: 'end',
                    loading: true,
                    loadingMessage: undefined,
                  });

                  // Announce the completed response to screen readers via the
                  // aria-live region in index.html. Strip markdown so only plain
                  // text is sent — HTML inside live regions is unreliably announced.
                  const finalText =
                    typeof streamData.system_msg === 'string' &&
                    streamData.system_msg &&
                    !currentSystemMsg &&
                    streamData.system_msg !== 'Conversation processing complete.'
                      ? streamData.system_msg
                      : currentSystemMsg;
                  if (finalText) {
                    announce(t('srResponse', { text: stripMarkdown(finalText) }));
                  }
                  break;
                }

                case 'error':
                case 'critical': {
                  streamEnded = true;
                  responseStatus = 'error';
                  responseErrorType = 'backend';
                  // Use the specific error message from the backend instead of generic one
                  const errorMessage = streamData.system_msg || t('errors.generic');
                  setMessages((prevMsgs) =>
                    prevMsgs.map((msg) =>
                      msg.id === botMsgId
                        ? {
                            ...msg,
                            text: errorMessage,
                            isLoading: false,
                            loadingMessage: '',
                          }
                        : msg
                    )
                  );
                  announce(forSpeech(stripMarkdown(errorMessage)));
                  break;
                }
              }
            } catch (e) {
              console.error('Error parsing stream event:', e);
            }
          },
        });

        // A backgrounded mobile browser can leave the socket dead without
        // failing the read. The backend sends a keepalive every 15s, so a
        // stream that has been silent for longer than that when the page comes
        // back is gone: close it, and the turn is read back below.
        let lastActivity = Date.now();
        const closeIfStaleOnReturn = () => {
          if (document.visibilityState === 'visible' && Date.now() - lastActivity > 20_000) {
            reader.cancel().catch(() => {});
          }
        };
        document.addEventListener('visibilitychange', closeIfStaleOnReturn);
        try {
          while (!done && !streamEnded) {
            const { value, done: doneReading } = await reader.read();
            done = doneReading;
            if (value) {
              lastActivity = Date.now();
              parser.feed(decoder.decode(value, { stream: !done }));
            }
          }
          if (streamEnded) {
            // The terminal event already committed the answer or error, so
            // this cancel only closes the transport. Keep a failure here from
            // reaching the catch below and replacing the finished response.
            try {
              await reader.cancel();
            } catch (cancelErr) {
              console.warn('Chat stream cleanup failed after terminal event:', cancelErr);
            }
          }
        } finally {
          document.removeEventListener('visibilitychange', closeIfStaleOnReturn);
          reader.releaseLock();
        }
        // Closed without an `end` frame: read the answer back if it was saved,
        // and otherwise settle as the failure it is rather than as a success.
        if (!streamEnded && isCurrent() && !(await recoverTurn()) && owns()) {
          const incomplete = new Error('Stream closed before its end frame');
          incomplete.name = 'IncompleteStream';
          throw incomplete;
        }
      } catch (err: any) {
        if (!owns()) return;
        if (err instanceof ApiError) httpFailure = err;
        console.warn('Chat stream error:', err, {
          status: httpFailure?.status ?? null,
          correlationId: httpFailure?.correlationId ?? null,
        });
        if ((await recoverTurn()) || !owns()) return;

        // Provide friendlier error message based on error type
        let userErrorMessage = t('errors.generic');
        responseStatus = 'error';
        responseErrorType = 'generic';

        const limitMessage = chatLimitMessage(err, t, i18n.language);

        if (err.name === 'IncompleteStream') {
          responseErrorType = 'incomplete_stream';
        } else if (limitMessage) {
          userErrorMessage = limitMessage;
          responseErrorType = 'rate_limited';
        } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
          userErrorMessage = t('errors.connectionLost');
          responseErrorType = 'connection_lost';
        } else if (err.name === 'AbortError') {
          userErrorMessage = t('errors.cancelled');
          responseStatus = 'cancelled';
          responseErrorType = null;
        } else if (err.message && err.message.includes('timeout')) {
          userErrorMessage = t('errors.timeout');
          responseErrorType = 'timeout';
        } else if (err.message && err.message.includes('network')) {
          userErrorMessage = t('errors.networkError');
          responseErrorType = 'network';
        }

        setMessages((prevMsgs) =>
          prevMsgs.map((msg) =>
            msg.id === botMsgId
              ? {
                  ...msg,
                  text: userErrorMessage,
                  isLoading: false,
                  loadingMessage: '',
                }
              : msg
          )
        );
        setStreamState({
          event: 'end',
          loading: false,
          loadingMessage: t('errors.initializing'),
        });
        // The bubble is no live region, so a screen reader learns of the
        // failure only from this; Stop announces its own cancellation.
        if (responseStatus === 'error') announce(forSpeech(userErrorMessage));
      } finally {
        if (liveTurnRef.current === liveTurn) liveTurnRef.current = null;
        if (controller.signal.aborted || stoppedWhileRecovering() || liveTurn.stopRequested) {
          responseStatus = 'cancelled';
          responseErrorType = null;
        }
        const cleanupTime = performance.now();
        const totalTime = cleanupTime - requestStartTime;
        analyticsRef.current.track('Chat Response Received', {
          chat_id: conversationIdRef.current,
          total_response_time_ms: totalTime,
          status: responseStatus,
          ...(responseErrorType ? { error_type: responseErrorType } : {}),
          // The stream was cut off and the answer was read back afterwards.
          ...(recovered ? { recovered: true } : {}),
          // The API turned the send away: the status, and the id that finds the
          // request in the API's or Front Door's logs.
          ...(httpFailure ? { http_status: httpFailure.status } : {}),
          ...(httpFailure?.correlationId ? { correlation_id: httpFailure.correlationId } : {}),
          // A send turned away once and repeated (see `chatSendRetry`).
          ...(retried ? { retried: true } : {}),
        });
        if (owns()) {
          abortRef.current = null;
          setIsResponding(false);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMsgId ? { ...msg, isLoading: false, loadingMessage: undefined } : msg
            )
          );
          setStreamState((prev) => ({ ...prev, loading: false }));
          setIsProcessing(false);
        }
      }
    },
    // Stable deps only — mutable values read from refs above. Research params
    // are included because they're read directly (not via a ref) so the
    // callback needs to re-bind when they change, e.g. when the user lands
    // on a research link mid-session or when their user record loads.
    [
      trackMessageSent,
      setIsProcessing,
      researchCampaignCode,
      researchParticipantId,
      query,
      isGuestMode,
      navigate,
      announce,
      t,
      i18n.language,
      depth,
      sendStop,
    ]
  );

  const stopResponse = useCallback(() => {
    const controller = abortRef.current;
    if (!controller || controller.signal.aborted) return;
    abortRef.current = null;
    // Closing the stream no longer stops the turn — it finishes server-side
    // for an app sent to the background — so the Stop is sent as a request of
    // its own, with how much of the answer the voter had on screen. A Stop
    // pressed before the `start` frame keeps reading (the UI already settled)
    // just long enough to learn the turn's id.
    const live = liveTurnRef.current;
    liveTurnRef.current = null;
    if (live && !live.stopRequested) {
      const showing = messagesRef.current.find((m) => m.sender === 'bot' && m.isLoading);
      live.stopRequested = true;
      live.shownChars = showing?.text.length ?? 0;
      if (live.turnId) {
        sendStop(live);
      } else {
        live.stopTimer = setTimeout(() => live.controller.abort(), STOP_AWAIT_START_MS);
      }
    }
    if (controller !== live?.controller) controller.abort();
    analyticsRef.current.track('Chat Response Stopped', { chat_id: conversationIdRef.current });
    announce(t('errors.cancelled'));
    setMessages((prev) =>
      prev.map((msg) =>
        msg.sender === 'bot' && msg.isLoading
          ? {
              ...msg,
              text: msg.text || t('errors.cancelled'),
              noAnswer: !msg.text,
              isLoading: false,
              loadingMessage: undefined,
            }
          : msg
      )
    );
    setStreamState({ event: 'end', loading: false, loadingMessage: undefined });
    setIsResponding(false);
    setIsProcessing(false);
  }, [announce, sendStop, setIsProcessing, t]);

  const handleDepthChange = useCallback(
    (next: AnswerDepth) => {
      // Tracked outside the state updater: React invokes updaters twice under
      // StrictMode, which would double-count every change.
      analyticsRef.current.track('Chat Answer Depth Selected', {
        depth: next,
        previousDepth: depth,
      });
      setDepth(next);
    },
    [depth]
  );

  const handleFollowupClick = useCallback(
    (suggestion: SuggestedPrompt, rank: number) => {
      analytics.track('Suggested Prompt Tapped', {
        promptId: suggestion.id,
        source: suggestion.source,
        rank,
        category: suggestion.category,
        // Separates the follow-up row from the landing row in the funnel while
        // keeping one event name across both surfaces and both platforms.
        surface: 'followup',
      });
      rememberAsked(suggestion.id);
      handleSend(suggestion.prompt);
    },
    [analytics, rememberAsked, handleSend]
  );

  const handleSuggestionClick = useCallback(
    async (suggestion: string, promptId?: string) => {
      rememberAsked(promptId);
      try {
        // Auto-open ballot panel for ballot-related suggestions
        if (suggestion.toLowerCase().includes('sample ballot') && !ballotOpenRef.current) {
          setBallotOpen(true);
          analyticsRef.current.track('Sample Ballot Viewed', {
            source: 'Suggestion Clicked',
          });
          mobileLayout.setBallotOpen(true);
        }
        await handleSend(suggestion);
      } catch (error) {
        console.error('💥 Error in handleSuggestionClick:', error);
        throw error;
      }
    },
    [handleSend, mobileLayout, rememberAsked]
  );

  const handleFeedbackSubmit = async (
    messageId: string,
    feedbackType: 'thumbs_up' | 'thumbs_down',
    feedback: string
  ) => {
    // Show confirmation immediately (non-blocking)
    setFeedbackConfirmations((prev) => ({ ...prev, [messageId]: feedbackType }));

    try {
      console.log(
        `Sending ${feedbackType} feedback for message ${messageId}`,
        feedback ? `with comment: ${feedback}` : 'without comment'
      );

      // Validate the feedback data before sending
      const feedbackData = {
        conversation_id: conversationId,
        message_id: messageId,
        feedback_type: feedbackType,
        comment: feedback || undefined,
      };

      // Validate with Zod schema
      const validatedData = feedbackSchema.parse(feedbackData);
      await query(`/chat/feedback`, { method: 'POST', body: JSON.stringify(validatedData) });
    } catch (error) {
      console.error('Error sending feedback:', error);
      // For now, we'll just log the error. In production, you might want to show a user-friendly
      // message
    }
  };

  useEffect(() => {
    registerChatHandler(handleSend);
  }, [handleSend, registerChatHandler]);

  const handleMenuClick = () => {
    setLeftPaneOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    setLeftPaneOpen(false);
  };

  // Ballot handlers
  const handleToggleBallot = (source: string) => {
    setBallotOpen((prev) => {
      const newBallotOpen = !prev;
      mobileLayout.setBallotOpen(newBallotOpen);
      // Only fire the view event when actually opening. Previously this
      // fired on every toggle (open AND close), inflating ``Sample Ballot
      // Viewed`` by ~2x relative to real opens. Closes are silent — if
      // we want a close signal later, it should be its own event.
      if (newBallotOpen) {
        analytics.track('Sample Ballot Viewed', { source });
      }
      return newBallotOpen;
    });
  };

  if (isAuthInitializing) {
    return <PageLoader />;
  }

  const hasUserMessages = messages.some((m) => m.sender === 'user');

  return (
    <>
      <DashboardLayoutAccountSidebar
        showWhiteStrip={true}
        onMenuClick={handleMenuClick}
        guestMode={effectiveGuestMode}
        ballotOpen={ballotOpen}
        onToggleBallot={() => handleToggleBallot('Right Menu')}
        onAccountIconClick={() => {
          setLeftPaneOpen(true);
          setTriggerLeftPaneAccountMenu(true);
        }}
        menuOpen={leftPaneOpen}
        hideLogo={!hasUserMessages}
      >
        <ChatWindow
          messages={messages}
          suggestions={suggestions}
          onSend={handleSend}
          onStop={stopResponse}
          isResponding={isResponding}
          onSuggestionClick={handleSuggestionClick}
          onFollowupClick={handleFollowupClick}
          disabled={streamState.loading}
          isHistoricalConversation={isHistoricalConversation}
          onStartNewConversation={startNewConversation}
          onFeedbackSubmit={handleFeedbackSubmit}
          feedbackConfirmations={feedbackConfirmations}
          conversationId={conversationId}
          userId={user?.user_id}
          streamState={streamState}
          isBallotOpen={ballotOpen}
          userName={userFirstName}
          isReturningUser={!!user}
          depth={depth}
          onDepthChange={handleDepthChange}
        />
        {/* Left pane menu lives INSIDE the layout's children so it
            sits inside the BallotSheetProvider tree (the provider is
            mounted in DashboardLayout). The menu's MUI Drawer portals
            to ``document.body`` for visual positioning, so the DOM
            location is unaffected by the React-tree move — but its
            ``useBallotSheet()`` calls now resolve to the real
            provider instead of the default-context noops. */}
        <LeftPaneMenu
          isOpen={leftPaneOpen}
          onClose={handleMenuClose}
          guestMode={effectiveGuestMode}
          onOpenBallot={() => {
            // Open ballot on desktop
            if (!ballotOpen) {
              handleToggleBallot('Left Menu - Chat Page');
            }
          }}
          shouldOpenAccountMenu={triggerLeftPaneAccountMenu}
          onAccountMenuOpened={() => setTriggerLeftPaneAccountMenu(false)}
        />
      </DashboardLayoutAccountSidebar>
    </>
  );
};

export default ChatContainer;
